import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Heart,
  Zap,
  Moon,
  Scale,
  Droplets,
  Brain,
  User,
  RefreshCw,
  Wind,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import BottomNavBar from "../../components/BottomNavBar";
import BridgeResponseDialog from "../../components/BridgeResponseDialog";
import HomeDateCalendar from "../../components/HomePage/HomeDateCalendar";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import welcomeBg from "../../assets/homebg.png";
import { cn } from "../../lib/utils";
import trendsService from "../../services/TrendsService";
import { useGoogleHealthSync } from "../../hooks/useGoogleHealthSync";
import {
  emptyTrendsViewModel,
  getTrendsWeightDisplayParts,
  formatTrendsLatestMealMeta,
  mapTrendsApiToViewModel,
} from "../../utils/trendsPageMappers";
import {
  mapHealthSyncApiToViewModel,
  mergeFitnessTrendsViewModel,
} from "../../utils/healthSyncMapper";
import { mergeGoogleHealthIntoTrendsViewModel } from "../../utils/googleHealthTrendsMapper";
import {
  formatHomeSelectedDate,
  getLocalDateString,
} from "../../utils/homePageUtils";
import { downloadWeeklyReport } from "../../utils/weeklyReportPdf";
import {
  gluceraFormControl,
  gluceraTrendsCardLabel,
  gluceraTrendsCardLabelWide,
  gluceraTrendsCareHero,
  gluceraTrendsChartDay,
  gluceraTrendsMetricLg,
  gluceraTrendsMetricMd,
  gluceraTrendsMetricMeta,
  gluceraTrendsMetricUnit,
  gluceraTrendsRowUnit,
  gluceraTrendsRowValue,
  gluceraTrendsSectionBadge,
  gluceraTrendsSectionNum,
  gluceraTrendsSectionTitle,
  gluceraTrendsStatLabel,
  gluceraTrendsSubtitle,
  gluceraTrendsTitle,
} from "../../utils/gluceraTypography";
import {
  loadUserFitnessGoals,
  parseGoalInput,
  parseSleepGoalInput,
  saveUserFitnessGoals,
  type UserFitnessGoals,
} from "../../utils/fitnessGoalsStorage";
import {
  HOME_BLUE,
  HOME_BLUE_CHART,
  HOME_BLUE_CHART_DEEP,
  HOME_BLUE_CHART_MID,
  homeAccentChip,
  homeMetricIconDeep,
  homeMetricIconMid,
  homeMetricIconPrimary,
  homeSectionBadge,
  homeSyncButton,
  homeTrendUpBadge,
  homeValuePositive,
} from "../../utils/homeBlueTheme";

/* ─── Mini Bar Chart (with Y-axis) ─────────────────────────── */
const niceAxisMax = (value: number): number => {
  if (value <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

const buildYAxisTicks = (dataMax: number): { yMax: number; ticks: number[] } => {
  const rawMax = Math.max(dataMax, 1);
  const yMax = niceAxisMax(rawMax);
  const step = Math.max(niceAxisMax(yMax / 4), 1);
  const ticks: number[] = [];
  for (let v = 0; v <= yMax; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
  return { yMax, ticks };
};

const formatAxisValue = (value: number): string => {
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(Math.round(value));
};

const defaultBarValueLabel = (value: number): string => {
  if (value <= 0) return "0";
  if (value >= 10000) return `${(value / 1000).toFixed(0)}k`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const MiniBarChart = ({
  data = [],
  color = HOME_BLUE_CHART,
  goal,
  showYAxis = false,
  formatValue = defaultBarValueLabel,
}: {
  data?: { day: string; value: number }[];
  color?: string;
  goal?: number;
  showYAxis?: boolean;
  formatValue?: (value: number) => string;
}) => {
  const safeData = Array.isArray(data) ? data : [];
  if (safeData.length === 0) return null;

  const dataMax = Math.max(...safeData.map((d) => d.value), 0);
  const { yMax, ticks } = buildYAxisTicks(dataMax);
  const scaleMax = showYAxis ? yMax : goal ?? Math.max(dataMax, 1);

  if (!showYAxis) {
    return (
      <div className="flex w-full items-end gap-1">
        {safeData.map((d, i) => {
          const pct = scaleMax > 0 ? Math.min((d.value / scaleMax) * 100, 100) : 0;
          return (
            <div key={`${d.day}-${i}`} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
              <span className="text-[9px] font-semibold tabular-nums text-foreground/80">
                {formatValue(d.value)}
              </span>
              <div className="flex h-14 w-full items-end">
                <div
                  className="w-full rounded-t-sm transition-all duration-500"
                  style={{
                    height: `${Math.max(pct, d.value > 0 ? 8 : 3)}%`,
                    backgroundColor: d.value > 0 ? color : "#e5e7eb",
                    opacity: i === safeData.length - 1 ? 1 : 0.75,
                  }}
                />
              </div>
              <span className={cn(gluceraTrendsChartDay, "text-center")}>{d.day}</span>
            </div>
          );
        })}
      </div>
    );
  }

  const svgW = 280;
  const svgH = 132;
  const marginLeft = 34;
  const marginRight = 6;
  const marginTop = 14;
  const marginBottom = 20;
  const chartW = svgW - marginLeft - marginRight;
  const chartH = svgH - marginTop - marginBottom;
  const barCount = safeData.length;
  const gap = 3;
  const barW = barCount > 0 ? (chartW - gap * (barCount - 1)) / barCount : 0;
  const toY = (v: number) => marginTop + chartH - (v / yMax) * chartH;

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ height: svgH }}>
      {ticks.map((v) => (
        <g key={v}>
          <text x={marginLeft - 4} y={toY(v) + 3.5} textAnchor="end" fontSize={9} fill="#9ca3af">
            {formatAxisValue(v)}
          </text>
          <line
            x1={marginLeft}
            y1={toY(v)}
            x2={svgW - marginRight}
            y2={toY(v)}
            stroke="#e5e7eb"
            strokeWidth={0.5}
          />
        </g>
      ))}

      <line
        x1={marginLeft}
        y1={marginTop}
        x2={marginLeft}
        y2={marginTop + chartH}
        stroke="#d1d5db"
        strokeWidth={0.8}
      />

      {safeData.map((d, i) => {
        const hasData = d.value > 0;
        const barH = hasData ? Math.max((d.value / yMax) * chartH, 2) : 3;
        const x = marginLeft + i * (barW + gap);
        const y = marginTop + chartH - barH;
        return (
          <g key={`${d.day}-${i}`}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={barH}
              rx={2}
              fill={hasData ? color : "#e5e7eb"}
              opacity={hasData ? (i === safeData.length - 1 ? 1 : 0.75) : 0.5}
            />
            <text
              x={x + barW / 2}
              y={hasData ? Math.max(y - 3, marginTop + 9) : marginTop + chartH - 6}
              textAnchor="middle"
              fontSize={8}
              fontWeight={600}
              fill={hasData ? color : "#9ca3af"}
            >
              {formatValue(d.value)}
            </text>
            <text
              x={x + barW / 2}
              y={marginTop + chartH + 12}
              textAnchor="middle"
              fontSize={9}
              fill="#9ca3af"
            >
              {d.day}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ─── BBS Trend Chart (bar chart with Y axis labels) ─────── */
const BbsTrendChart = ({
  data = [],
  color = HOME_BLUE,
  goal = 100,
  formatValue = (v) => (v > 0 ? String(Math.round(v)) : "0"),
}: {
  data?: { day: string; value: number }[];
  color?: string;
  goal?: number;
  formatValue?: (value: number) => string;
}) => {
  const safeData = Array.isArray(data) ? data : [];
  if (safeData.length === 0) return null;

  const svgW = 280;
  const svgH = 122;
  const marginLeft = 30;
  const marginRight = 6;
  const marginTop = 14;
  const marginBottom = 20;
  const chartW = svgW - marginLeft - marginRight;
  const chartH = svgH - marginTop - marginBottom;

  const yMax = goal > 0 ? goal : 100;
  const yTicks = [0, 25, 50, 75, 100].filter((v) => v <= yMax);

  const barCount = safeData.length;
  const gap = 3;
  const barW = barCount > 0 ? (chartW - gap * (barCount - 1)) / barCount : 0;

  const toY = (v: number) => marginTop + chartH - (v / yMax) * chartH;

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ height: svgH }}>
      {/* Y grid lines + labels */}
      {yTicks.map((v) => (
        <g key={v}>
          <text x={marginLeft - 4} y={toY(v) + 3.5} textAnchor="end" fontSize={9} fill="#9ca3af">
            {v}
          </text>
          <line
            x1={marginLeft} y1={toY(v)}
            x2={svgW - marginRight} y2={toY(v)}
            stroke="#e5e7eb" strokeWidth={0.5}
          />
        </g>
      ))}

      {/* Y axis line */}
      <line
        x1={marginLeft} y1={marginTop}
        x2={marginLeft} y2={marginTop + chartH}
        stroke="#d1d5db" strokeWidth={0.8}
      />

      {/* Bars */}
      {safeData.map((d, i) => {
        const pct = yMax > 0 ? Math.min(d.value / yMax, 1) : 0;
        const hasData = d.value > 0;
        const barH = hasData ? Math.max(pct * chartH, 2) : 3;
        const x = marginLeft + i * (barW + gap);
        const y = marginTop + chartH - barH;
        return (
          <g key={i}>
            <rect
              x={x} y={y}
              width={barW} height={barH}
              rx={2}
              fill={hasData ? color : "#e5e7eb"}
              opacity={hasData ? (i === safeData.length - 1 ? 1 : 0.6) : 0.5}
            />
            <text
              x={x + barW / 2}
              y={hasData ? Math.max(y - 3, marginTop + 9) : marginTop + chartH - 6}
              textAnchor="middle"
              fontSize={8}
              fontWeight={600}
              fill={hasData ? color : "#9ca3af"}
            >
              {formatValue(d.value)}
            </text>
            <text
              x={x + barW / 2} y={marginTop + chartH + 12}
              textAnchor="middle" fontSize={9} fill="#9ca3af"
            >
              {d.day}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ─── Mini Line Chart ─────────────────────────────────────── */
const MiniLineChart = ({
  data = [],
  color = HOME_BLUE_CHART,
  formatValue = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1)),
}: {
  data?: number[];
  color?: string;
  formatValue?: (value: number) => string;
}) => {
  const safeData = Array.isArray(data) ? data.filter((v) => Number.isFinite(v)) : [];
  if (safeData.length < 2) return null;
  const w = 280;
  const h = 72;
  const padTop = 12;
  const chartH = h - padTop;
  const min = Math.min(...safeData) - 1;
  const max = Math.max(...safeData) + 1;
  const toX = (i: number) => (i / (safeData.length - 1)) * w;
  const toY = (v: number) => padTop + chartH - ((v - min) / (max - min || 1)) * chartH;
  const pts = safeData.map((v, i) => `${toX(i)},${toY(v)}`).join(" ");
  const area = `M ${toX(0)},${padTop + chartH} L ${pts} L ${toX(safeData.length - 1)},${padTop + chartH} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: h }}>
      <path d={area} fill={color} fillOpacity={0.1} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {safeData.map((v, i) => (
        <g key={i}>
          <circle cx={toX(i)} cy={toY(v)} r={i === safeData.length - 1 ? 4 : 3} fill={color} />
          <text
            x={toX(i)}
            y={toY(v) - 6}
            textAnchor="middle"
            fontSize={8}
            fontWeight={600}
            fill={color}
          >
            {formatValue(v)}
          </text>
        </g>
      ))}
    </svg>
  );
};

/* ─── Weight Trend Chart (line chart with day labels) ─────── */
const WeightTrendChart = ({
  data = [],
}: {
  data?: { day: string; value: number }[];
}) => {
  const pts = (Array.isArray(data) ? data : []).filter(
    (d) => Number.isFinite(d.value) && d.value > 0
  );
  if (pts.length < 2) return null;

  const w = 280;
  const h = 96;
  const padTop = 16;
  const padBottom = 18;
  const padLeft = 8;
  const padRight = 8;
  const chartH = h - padTop - padBottom;
  const chartW = w - padLeft - padRight;

  const values = pts.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  // Pad the range so a near-flat line isn't glued to the top/bottom edge.
  const yMin = min - span * 0.25;
  const yMax = max + span * 0.25;

  const toX = (i: number) => padLeft + (i / (pts.length - 1)) * chartW;
  const toY = (v: number) =>
    padTop + chartH - ((v - yMin) / (yMax - yMin || 1)) * chartH;

  const line = pts.map((p, i) => `${toX(i)},${toY(p.value)}`).join(" ");
  const area = `M ${toX(0)},${padTop + chartH} L ${line} L ${toX(pts.length - 1)},${padTop + chartH} Z`;
  const color = HOME_BLUE;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: h }}>
      <path d={area} fill={color} fillOpacity={0.1} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {pts.map((p, i) => (
        <g key={`${p.day}-${i}`}>
          <circle
            cx={toX(i)}
            cy={toY(p.value)}
            r={i === pts.length - 1 ? 4 : 3}
            fill={color}
          />
          <text
            x={toX(i)}
            y={toY(p.value) - 6}
            textAnchor="middle"
            fontSize={8}
            fontWeight={600}
            fill={color}
          >
            {Number.isInteger(p.value) ? p.value : p.value.toFixed(1)}
          </text>
          <text x={toX(i)} y={h - 4} textAnchor="middle" fontSize={9} fill="#9ca3af">
            {p.day}
          </text>
        </g>
      ))}
    </svg>
  );
};

/* ─── Glucose Spike Chart (with X/Y axes) ─────────────────── */
const GlucoseSpikeChart = ({
  points = [],
  color = HOME_BLUE,
  threshold = 140,
}: {
  points?: { time_min: number; glucose_mg_dl: number }[];
  color?: string;
  threshold?: number;
}) => {
  const safePoints = Array.isArray(points)
    ? points.filter((p) => Number.isFinite(p.glucose_mg_dl) && Number.isFinite(p.time_min))
    : [];
  if (safePoints.length < 2) return null;

  const svgW = 280;
  const svgH = 150;
  const marginLeft = 38;
  const marginRight = 8;
  const marginTop = 20;
  const marginBottom = 32;
  const chartW = svgW - marginLeft - marginRight;
  const chartH = svgH - marginTop - marginBottom;

  const glucoseVals = safePoints.map((p) => p.glucose_mg_dl);
  const timeVals = safePoints.map((p) => p.time_min);
  const step = 20;
  const yMin = Math.floor(Math.min(...glucoseVals) / step) * step;
  const yMax = Math.ceil(Math.max(...glucoseVals) / step) * step;
  const xMin = Math.min(...timeVals);
  const xMax = Math.max(...timeVals);

  const toX = (t: number) => marginLeft + ((t - xMin) / (xMax - xMin || 1)) * chartW;
  const toY = (g: number) => marginTop + chartH - ((g - yMin) / (yMax - yMin || 1)) * chartH;

  const yTicks: number[] = [];
  for (let v = yMin; v <= yMax; v += step) yTicks.push(v);

  const linePoints = safePoints.map((p) => `${toX(p.time_min)},${toY(p.glucose_mg_dl)}`).join(" ");
  const areaPath =
    `M ${toX(safePoints[0].time_min)},${marginTop + chartH} ` +
    safePoints.map((p) => `L ${toX(p.time_min)},${toY(p.glucose_mg_dl)}`).join(" ") +
    ` L ${toX(safePoints[safePoints.length - 1].time_min)},${marginTop + chartH} Z`;

  const peak = safePoints.reduce((a, b) => (b.glucose_mg_dl > a.glucose_mg_dl ? b : a));
  const showThreshold = threshold >= yMin && threshold <= yMax;
  const thresholdY = toY(threshold);

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ height: svgH }}>
      {/* Y axis grid lines + labels */}
      {yTicks.map((v) => (
        <g key={v}>
          <text x={marginLeft - 5} y={toY(v) + 3.5} textAnchor="end" fontSize={9} fill="#9ca3af">
            {v}
          </text>
          <line
            x1={marginLeft} y1={toY(v)}
            x2={svgW - marginRight} y2={toY(v)}
            stroke="#e5e7eb" strokeWidth={0.5}
          />
        </g>
      ))}

      {/* Y axis line */}
      <line
        x1={marginLeft} y1={marginTop}
        x2={marginLeft} y2={marginTop + chartH}
        stroke="#d1d5db" strokeWidth={0.8}
      />

      {/* Threshold dashed line */}
      {showThreshold && (
        <line
          x1={marginLeft} y1={thresholdY}
          x2={svgW - marginRight} y2={thresholdY}
          stroke={color} strokeWidth={1} strokeDasharray="4,3" opacity={0.5}
        />
      )}

      {/* Area fill */}
      <path d={areaPath} fill={color} fillOpacity={0.12} />

      {/* Curve line */}
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Data point dots — hollow for all, filled for peak */}
      {safePoints.map((p) => {
        const isPeak = p.time_min === peak.time_min && p.glucose_mg_dl === peak.glucose_mg_dl;
        const cx = toX(p.time_min);
        const cy = toY(p.glucose_mg_dl);
        // Glucose label — above for peak, below for others (avoid overlap with curve)
        const labelY = isPeak ? cy - 8 : cy + 14;
        return (
          <g key={p.time_min}>
            {isPeak ? (
              <circle cx={cx} cy={cy} r={5} fill={color} />
            ) : (
              <>
                <circle cx={cx} cy={cy} r={4.5} fill="white" stroke={color} strokeWidth={1.5} />
              </>
            )}
            <text
              x={cx} y={labelY}
              textAnchor="middle"
              fontSize={8.5}
              fontWeight={isPeak ? "700" : "400"}
              fill={isPeak ? color : "#6b7280"}
            >
              {Math.round(p.glucose_mg_dl)}
            </text>
          </g>
        );
      })}

      {/* X axis baseline */}
      <line
        x1={marginLeft} y1={marginTop + chartH}
        x2={svgW - marginRight} y2={marginTop + chartH}
        stroke="#d1d5db" strokeWidth={0.8}
      />

      {/* X axis ticks + labels (actual time_min values) */}
      {safePoints.map((p) => (
        <g key={p.time_min}>
          <line
            x1={toX(p.time_min)} y1={marginTop + chartH}
            x2={toX(p.time_min)} y2={marginTop + chartH + 4}
            stroke="#d1d5db" strokeWidth={0.8}
          />
          <text
            x={toX(p.time_min)} y={marginTop + chartH + 13}
            textAnchor="middle" fontSize={9} fill="#9ca3af"
          >
            {p.time_min}
          </text>
        </g>
      ))}

      {/* X axis "Time (min)" label */}
      <text
        x={marginLeft + chartW / 2}
        y={svgH - 2}
        textAnchor="middle"
        fontSize={8}
        fill="#9ca3af"
      >
        Time (min)
      </text>
    </svg>
  );
};

/* ─── Helpers ─────────────────────────────────────────────── */
const fmt = (v: number | string | undefined, decimals = 0) =>
  Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: decimals });

const TrendsGoalField = ({
  title,
  hint,
  value,
  onCommit,
  parse = parseGoalInput,
  step = 1,
  unit,
}: {
  title: string;
  hint: string;
  value: number;
  onCommit: (next: number) => void;
  parse?: (raw: string) => number | null;
  step?: number;
  unit?: string;
}) => {
  const [draft, setDraft] = useState(String(value));
  const lastCommittedRef = useRef(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCommitRef = useRef(onCommit);
  const parseRef = useRef(parse);
  onCommitRef.current = onCommit;
  parseRef.current = parse;

  useEffect(() => {
    setDraft(String(value));
    lastCommittedRef.current = value;
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const commitValue = (raw: string, normalizeDraft: boolean) => {
    const parsed = parseRef.current(raw);
    if (parsed == null) {
      if (normalizeDraft) setDraft(String(lastCommittedRef.current));
      return;
    }
    if (normalizeDraft) setDraft(String(parsed));
    if (parsed === lastCommittedRef.current) return;
    lastCommittedRef.current = parsed;
    onCommitRef.current(parsed);
  };

  const scheduleCommit = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commitValue(raw, false), 700);
  };

  const commitNow = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    commitValue(raw, true);
  };

  return (
    <div className="mt-3 rounded-xl border border-border/70 bg-muted/35 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-compact-sm font-semibold text-foreground">{title}</p>
          <p className="mt-0.5 text-2xs leading-snug text-muted-foreground">{hint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Input
            type="number"
            inputMode={step < 1 ? "decimal" : "numeric"}
            min={step < 1 ? 0.5 : 1}
            step={step}
            value={draft}
            onChange={(e) => {
              const next = e.target.value;
              setDraft(next);
              scheduleCommit(next);
            }}
            onBlur={() => commitNow(draft)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            className={cn(
              gluceraFormControl,
              "h-9 w-[5.75rem] px-2 py-1 text-center text-compact-sm font-semibold tabular-nums",
            )}
            aria-label={title}
          />
          {unit ? (
            <span className="text-compact-sm font-medium text-muted-foreground">{unit}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────── */

const TrendsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Always start on the user's live local calendar day — never a sticky/stale ?date=.
  const [selectedDate, setSelectedDate] = useState(() => getLocalDateString());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    () => new Date(`${selectedDate}T12:00:00`),
  );
  const isSelectedDateToday = selectedDate === getLocalDateString();
  const formattedSelectedDate = formatHomeSelectedDate(selectedDate);
  const [goalOverrides, setGoalOverrides] = useState<UserFitnessGoals>(() =>
    loadUserFitnessGoals(),
  );
  const goalOverridesRef = useRef(goalOverrides);
  goalOverridesRef.current = goalOverrides;

  // Page load: force ?date= to local today (YYYY-MM-DD), not UTC or a cached prior day.
  useEffect(() => {
    const today = getLocalDateString();
    setSelectedDate(today);
    setCurrentMonth(new Date(`${today}T12:00:00`));
    if (searchParams.get("date") !== today) {
      setSearchParams({ date: today }, { replace: true });
    }
    // Calendar selections update date after mount; do not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadWeeklyReport(selectedDate);
      toast.success("Weekly report downloaded");
    } catch {
      toast.error("Couldn't create the weekly report. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDateSelect = (dateString: string) => {
    setSelectedDate(dateString);
    setSearchParams({ date: dateString }, { replace: true });
    setCurrentMonth(new Date(`${dateString}T12:00:00`));
    setIsCalendarOpen(false);
  };

  const { data: trendsApi, isLoading: isTrendsLoading } = useQuery({
    queryKey: ["trends-page", selectedDate],
    queryFn: () => trendsService.getTrends(selectedDate),
    staleTime: 60 * 1000,
    // Always refresh on entry so we never paint a prior day's react-query cache as "today".
    refetchOnMount: "always",
  });

  const healthSyncGoals = useMemo(() => {
    const base = mapTrendsApiToViewModel(trendsApi);
    return {
      steps_goal: goalOverrides.steps_goal ?? base.daily.steps_goal,
      calories_goal: goalOverrides.calories_goal ?? base.daily.calories_goal,
    };
  }, [trendsApi, goalOverrides]);

  const {
    healthSyncApi,
    healthPayload,
    isSyncing,
    isBridgeLoading,
    isHealthSyncLoading,
    hasBridgeHealthData,
    hasGoogleHealthData,
    syncGoogleHealth,
    persistGoalsToBackend,
    debugPopupOpen,
    debugPopupLoading,
    debugPopupData,
    closeDebugPopup,
  } = useGoogleHealthSync({ date: selectedDate, goals: healthSyncGoals });

  /** Sleep stays local-only (not in health-sync payload). */
  const commitSleepGoal = useCallback((next: number) => {
    const updated = { ...goalOverridesRef.current, sleep_goal: next };
    goalOverridesRef.current = updated;
    saveUserFitnessGoals(updated);
    setGoalOverrides(updated);
  }, []);

  /**
   * Steps / calories goals → localStorage + POST /health-sync/
   * with steps_goal / calories_goal on health_data (curl contract).
   */
  const commitSyncGoal = useCallback(
    (key: "steps_goal" | "calories_goal", next: number) => {
      const base = mapTrendsApiToViewModel(trendsApi);
      const updated: UserFitnessGoals = {
        ...goalOverridesRef.current,
        [key]: next,
      };
      goalOverridesRef.current = updated;
      saveUserFitnessGoals(updated);
      setGoalOverrides(updated);

      void persistGoalsToBackend({
        steps_goal: updated.steps_goal ?? base.daily.steps_goal,
        calories_goal: updated.calories_goal ?? base.daily.calories_goal,
      });
    },
    [persistGoalsToBackend, trendsApi],
  );

  const waitingForBridge = isBridgeLoading && !hasBridgeHealthData;
  const loadingFitnessFallback = !hasBridgeHealthData && isHealthSyncLoading;
  const isPageLoading = isTrendsLoading || waitingForBridge || loadingFitnessFallback;

  const vm = useMemo(() => {
    const base = mapTrendsApiToViewModel(trendsApi) ?? emptyTrendsViewModel();

    // Apply health-sync first so today_overview.distance (meters) is kept.
    // Bridge overlay only replaces fields when it has a positive value.
    const syncDate =
      healthSyncApi?.date_local ||
      healthSyncApi?.selected_date ||
      healthSyncApi?.today_local ||
      healthSyncApi?.today_overview?.date;
    const healthForSelectedDate =
      !syncDate || syncDate === selectedDate ? healthSyncApi : null;
    const healthVm = mapHealthSyncApiToViewModel(healthForSelectedDate);
    const withHealthSync = mergeFitnessTrendsViewModel(base, healthVm);

    if (healthPayload?.health_data?.length) {
      return mergeGoogleHealthIntoTrendsViewModel(
        withHealthSync,
        healthPayload,
        selectedDate,
      );
    }

    return withHealthSync;
  }, [trendsApi, healthPayload, healthSyncApi, selectedDate]);
  const daily = vm.daily;
  const stepsGoal = goalOverrides.steps_goal ?? daily.steps_goal;
  const caloriesGoal = goalOverrides.calories_goal ?? daily.calories_goal;
  const sleepGoal = goalOverrides.sleep_goal ?? daily.sleep_goal;
  const stepsBarData = vm.stepsBarData ?? [];
  const calBarData = vm.calBarData ?? [];
  const hrvLineData = vm.hrvLineData ?? [];
  const sleepBarData = vm.sleepBarData ?? [];
  const bbsTrend = vm.bbsTrend ?? [];
  const glucoseCurveData = vm.glucoseCurveData ?? [];
  const glucoseCurveLabels = vm.glucoseCurveLabels ?? [];
  const glucoseCurvePoints = vm.glucoseCurvePoints ?? [];
  const glucoseMeta = vm.glucoseMeta ?? { spikeMgDl: 0 };
  const latestMeal = vm.latestMeal;
  const latestMealMeta = formatTrendsLatestMealMeta(latestMeal);
  const bbsLabel = vm.bbsLabel;
  const latestGlucose = vm.latestGlucose;
  const latestBbs = vm.latestBbs;
  const latestWeight = vm.latestWeight;
  const weightParts = getTrendsWeightDisplayParts(daily, latestWeight);
  const weightBarData = vm.weightBarData ?? [];
  const weeklyStepTotal = vm.weeklyStepTotal;
  const weeklyCalAvg = vm.weeklyCalAvg;

  const sleepHoursDisplay =
    daily.sleep_hours ??
    (daily.sleep_minutes > 0
      ? fmt(daily.sleep_minutes / 60, daily.sleep_minutes % 60 === 0 ? 0 : 1)
      : "0");
  const glucoseSpikeColor = HOME_BLUE;

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{ minHeight: "100vh", maxHeight: "100vh", overflow: "hidden" }}
    >
      {/* Blurred Background */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-background/80" />

      <div className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto px-4 pb-6 pt-2" style={{ height: "calc(100vh - 80px)" }}>

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-3 pb-2 pt-0.5">
          <div className="min-w-0 flex-1">
            <p className={gluceraTrendsSubtitle}>Fitness overview</p>
            <h1 className={cn("mt-1", gluceraTrendsTitle)}>Trends</h1>
            <p className={cn("mt-1 text-compact-sm text-muted-foreground")}>
              {isSelectedDateToday ? "Today" : formattedSelectedDate}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2 pt-0.5">
            <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={syncGoogleHealth}
              disabled={isSyncing}
              className={cn("flex h-10 items-center gap-1.5 rounded-xl border px-3 disabled:opacity-50", homeSyncButton)}
              aria-label="Sync Google Health"
            >
              <RefreshCw
                className={cn("h-4 w-4", homeMetricIconPrimary, isSyncing && "animate-spin")}
                strokeWidth={2}
              />
              <span className={cn("text-compact-xs font-semibold", homeMetricIconPrimary)}>Sync</span>
            </button>
            <HomeDateCalendar
              open={isCalendarOpen}
              onOpenChange={setIsCalendarOpen}
              currentMonth={currentMonth}
              onCurrentMonthChange={setCurrentMonth}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
            <button
              type="button"
              onClick={() => navigate("/glucera/profilepage")}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-muted"
              aria-label="Profile"
            >
              <User className={cn("h-5 w-5", homeMetricIconPrimary)} />
            </button>
            </div>
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className={cn("flex h-9 items-center gap-1.5 rounded-xl border px-3 disabled:opacity-50", homeSyncButton)}
              aria-label="Download weekly PDF report"
            >
              <Download className={cn("h-4 w-4", homeMetricIconPrimary)} strokeWidth={2} />
              <span className={cn("text-compact-xs font-semibold", homeMetricIconPrimary)}>
                {isExporting ? "Exporting" : "Export"}
              </span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {isPageLoading ? (
            <div className="space-y-3 pt-1">
              <Skeleton className="h-36 rounded-2xl" />
              <Skeleton className="h-44 rounded-2xl" />
              <Skeleton className="h-44 rounded-2xl" />
              <Skeleton className="h-52 rounded-2xl" />
            </div>
          ) : (
          <>
          {/* ══════════════════════════════════════════
              SECTION 01 — NUTRITION (Bite Balance is primary)
          ══════════════════════════════════════════ */}
          <div className="mt-0">
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-full", homeSectionBadge)}>
                  <span className={gluceraTrendsSectionNum}>01</span>
                </div>
                <div>
                  <h2 className={gluceraTrendsSectionTitle}>Nutrition</h2>
                </div>
              </div>
              <div className="mt-1 flex flex-col items-end gap-1">
                <span className={gluceraTrendsSectionBadge}>7-Day Trend</span>
                <p className={cn(gluceraTrendsCardLabel, "normal-case tracking-normal")}>{vm.dateLabel}</p>
              </div>
            </div>

            {/* Bite Balance Score — main card first */}
            <Card className="space-y-4 rounded-2xl border-0 bg-card p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className={gluceraTrendsCardLabel}>Bite Balance Score</p>
                  <p
                    className={cn(
                      "mt-0.5",
                      latestBbs > 0 ? gluceraTrendsMetricLg : gluceraTrendsMetricMeta,
                      latestBbs > 0 && homeValuePositive,
                    )}
                  >
                    {latestBbs > 0 ? (
                      <>
                        {Math.round(latestBbs)}
                        <span className={cn(gluceraTrendsMetricUnit, "ml-1")}>pts</span>
                      </>
                    ) : isSelectedDateToday ? (
                      "No score for today"
                    ) : (
                      "No score for this day"
                    )}
                  </p>
                  <p className={gluceraTrendsMetricMeta}>
                    {latestBbs > 0 && bbsLabel
                      ? `${bbsLabel} · 7-day trend`
                      : latestBbs > 0
                        ? "7-day trend"
                        : isSelectedDateToday
                          ? "Log meals to get today's score"
                          : "No BBS logged for this day"}
                  </p>
                </div>
                <TrendingUp className={cn("h-5 w-5", homeMetricIconPrimary)} />
              </div>
              {bbsTrend.some((d) => d.value > 0) ? (
                <BbsTrendChart data={bbsTrend} color={HOME_BLUE} goal={100} />
              ) : (
                <p className={cn(gluceraTrendsMetricMeta, "py-4 text-center")}>
                  Log meals to see BBS trend
                </p>
              )}
            </Card>

            {/* Glucose Spike */}
            <Card className="mt-3 space-y-4 rounded-2xl border-0 bg-card p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className={gluceraTrendsCardLabel}>Glucose Spike</p>
                  <p className={cn(gluceraTrendsMetricLg, "mt-0.5", homeValuePositive)}>
                    +{fmt(latestGlucose, 1)}
                    <span className={cn(gluceraTrendsMetricUnit, "ml-1")}>mg/dL</span>
                  </p>
                  <p className={gluceraTrendsMetricMeta}>
                    {latestMealMeta ?? glucoseMeta.title ?? "Latest meal curve"}
                  </p>
                </div>
                <Activity className={cn("h-5 w-5", homeMetricIconPrimary)} />
              </div>
              {glucoseCurvePoints.length >= 2 ? (
                <GlucoseSpikeChart
                  points={glucoseCurvePoints}
                  color={glucoseSpikeColor}
                  threshold={140}
                />
              ) : glucoseCurveData.length >= 2 ? (
                <>
                  <MiniLineChart data={glucoseCurveData} color={glucoseSpikeColor} />
                  <div className="flex gap-1.5 pt-1">
                    {(glucoseCurveLabels.length ? glucoseCurveLabels : glucoseCurveData.map((_, i) => `${i}`)).map(
                      (label, index) => (
                      <span key={`${label}-${index}`} className={cn("flex-1 text-center", gluceraTrendsChartDay)}>
                        {label}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <p className={cn(gluceraTrendsMetricMeta, "py-4 text-center")}>
                  {glucoseMeta.uiMessage ?? "Log a meal to see glucose curve"}
                </p>
              )}
            </Card>
          </div>

          {/* ── Today's Overview — activity snapshot ── */}
          <div className="mt-2 space-y-3">
            <div className="flex items-center justify-between px-0.5">
              <p className={gluceraTrendsCardLabelWide}>
                {isSelectedDateToday ? "Today's Overview" : "Daily Overview"}
              </p>
              {hasGoogleHealthData ? (
                <span className={cn("rounded-full px-2 py-0.5 text-2xs font-semibold", homeAccentChip)}>
                  Synced
                </span>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
                <div className="flex min-w-0 flex-col items-center gap-1.5">
                  <Moon className={cn("h-5 w-5 shrink-0", homeMetricIconDeep)} />
                  <p className={gluceraTrendsMetricMd}>{sleepHoursDisplay}</p>
                  <p className={cn(gluceraTrendsStatLabel, "text-center")}>Sleep</p>
                </div>
              </Card>

              <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
                <div className="flex min-w-0 flex-col items-center gap-1.5">
                  <Activity className={cn("h-5 w-5 shrink-0", homeMetricIconPrimary)} />
                  <p className={gluceraTrendsMetricMd}>{fmt(daily.steps)}</p>
                  <p className={cn(gluceraTrendsStatLabel, "text-center")}>Steps</p>
                </div>
              </Card>

              <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
                <div className="flex min-w-0 flex-col items-center gap-1.5">
                  <Zap className={cn("h-5 w-5 shrink-0", homeMetricIconMid)} />
                  <p className={gluceraTrendsMetricMd}>{fmt(daily.calories_burned)}</p>
                  <p className={cn(gluceraTrendsStatLabel, "text-center")}>Cal</p>
                </div>
              </Card>

              <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
                <div className="flex min-w-0 flex-col items-center gap-1.5">
                  <Heart className={cn("h-5 w-5 shrink-0", homeMetricIconDeep)} />
                  <p className={gluceraTrendsMetricMd}>{fmt(daily.active_zone_minutes)}</p>
                  <p className={cn(gluceraTrendsStatLabel, "text-center leading-tight")}>
                    Active Zone Minutes
                  </p>
                </div>
              </Card>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              SECTION 02 — PROGRESS
          ══════════════════════════════════════════ */}
          <div className="mt-2">
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-full", homeSectionBadge)}>
                  <span className={gluceraTrendsSectionNum}>02</span>
                </div>
                <div>
                  <h2 className={gluceraTrendsSectionTitle}>Progress</h2>
                </div>
              </div>
              <span className={cn(gluceraTrendsSectionBadge, "mt-1")}>7 Days</span>
            </div>

            {/* Sleep Chart */}
            <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className={gluceraTrendsCardLabel}>Weekly Sleep</p>
                  <p className={cn(gluceraTrendsMetricLg, "mt-0.5")}>{daily.sleep_hours ?? "0h 0 min"}</p>
                  <p className={gluceraTrendsMetricMeta}>Last night summary</p>
                </div>
                <Moon className={cn("h-5 w-5 shrink-0", homeMetricIconDeep)} />
              </div>
              {sleepBarData.length > 0 && (
                <MiniBarChart
                  data={sleepBarData}
                  color={HOME_BLUE_CHART_DEEP}
                  showYAxis
                  formatValue={(v) => (v > 0 ? `${v.toFixed(1)}h` : "0")}
                />
              )}
            </Card>

            {/* Steps Chart */}
            <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm space-y-4 mt-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className={gluceraTrendsCardLabel}>Weekly Steps</p>
                  <p className={cn(gluceraTrendsMetricLg, "mt-0.5")}>{fmt(weeklyStepTotal)}</p>
                  <p className={gluceraTrendsMetricMeta}>Weekly total</p>
                </div>
                <Activity className={cn("h-5 w-5 shrink-0", homeMetricIconPrimary)} />
              </div>
              <TrendsGoalField
                title="Step goal"
                hint="Steps you want every day"
                value={stepsGoal}
                onCommit={(next) => commitSyncGoal("steps_goal", next)}
                unit="steps"
              />
              {stepsBarData.length > 0 && (
                <MiniBarChart
                  data={stepsBarData}
                  color={HOME_BLUE_CHART}
                  goal={stepsGoal}
                  showYAxis
                />
              )}
            </Card>

            {/* Calories Chart */}
            <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm space-y-4 mt-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className={gluceraTrendsCardLabel}>Weekly Calories Burned</p>
                  <p className={cn(gluceraTrendsMetricLg, "mt-0.5")}>{fmt(weeklyCalAvg)}</p>
                  <p className={gluceraTrendsMetricMeta}>Daily average</p>
                </div>
                <Zap className={cn("h-5 w-5 shrink-0", homeMetricIconMid)} />
              </div>
              <TrendsGoalField
                title="Calorie goal"
                hint="Calories you want to burn daily"
                value={caloriesGoal}
                onCommit={(next) => commitSyncGoal("calories_goal", next)}
                unit="kcal"
              />
              {calBarData.length > 0 && (
                <MiniBarChart
                  data={calBarData}
                  color={HOME_BLUE_CHART_MID}
                  goal={caloriesGoal}
                  showYAxis
                />
              )}
            </Card>
          </div>
          {/* ══════════════════════════════════════════
              SECTION 03 — CARE
          ══════════════════════════════════════════ */}
          <div className="mt-2">
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-full", homeSectionBadge)}>
                  <span className={gluceraTrendsSectionNum}>03</span>
                </div>
                <div>
                  <h2 className={gluceraTrendsSectionTitle}>Care</h2>
                </div>
              </div>
              <span className={cn(gluceraTrendsSectionBadge, "mt-1")}>Clinical</span>
            </div>

            {/* Dark body metrics card */}
            <Card className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm space-y-4">
              <div>
                <p className={gluceraTrendsCardLabelWide}>Your body today</p>
              </div>

              <h2 className={gluceraTrendsCareHero}>
                Where you're{" "}
                <span className={homeMetricIconPrimary}>at.</span>
              </h2>

              <div className="space-y-3 border-t border-border/50 pt-4">
                {/* Weight with trend graph */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Scale className="h-4 w-4 text-muted-foreground" />
                      <span className={gluceraTrendsCardLabel}>Weight</span>
                    </div>
                    <span className={cn(gluceraTrendsRowValue, "text-right")}>
                      {weightParts.length === 0 ? (
                        "—"
                      ) : (
                        weightParts.map((part, i) => (
                          <span key={part.unit}>
                            {i > 0 ? (
                              <span className="mx-1.5 text-muted-foreground/60">·</span>
                            ) : null}
                            {fmt(part.value, 1)}
                            <span className={cn(gluceraTrendsRowUnit, "ml-1")}>{part.unit}</span>
                          </span>
                        ))
                      )}
                    </span>
                  </div>

                  {/* Weight trend — shows whether weight is going up or down */}
                  {(() => {
                    const pts = weightBarData.filter((d) => d.value > 0);
                    if (pts.length < 2) {
                      return null;
                    }
                    const first = pts[0].value;
                    const last = pts[pts.length - 1].value;
                    const delta = last - first;
                    const flat = Math.abs(delta) < 0.05;
                    const gaining = delta > 0;
                    const unit = daily.preferred_weight_unit ?? "kg";
                    return (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={gluceraTrendsMetricMeta}>7-day trend</span>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold",
                              flat
                                ? "bg-muted text-muted-foreground"
                                : homeTrendUpBadge
                            )}
                          >
                            {flat ? null : gaining ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : (
                              <TrendingDown className="h-3 w-3" />
                            )}
                            {flat
                              ? "Stable"
                              : `${gaining ? "+" : "−"}${Math.abs(delta).toFixed(1)} ${unit}`}
                          </span>
                        </div>
                        <WeightTrendChart data={weightBarData} />
                      </div>
                    );
                  })()}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-muted-foreground" />
                    <span className={gluceraTrendsCardLabel}>Heart Rate Variability</span>
                  </div>
                  <span className={gluceraTrendsRowValue}>
                    {daily?.hrv_rmssd != null
                      ? fmt(daily.hrv_rmssd, 2)
                      : "—"}
                    <span className={cn(gluceraTrendsRowUnit, "ml-1")}>ms</span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Droplets className="h-4 w-4 text-muted-foreground" />
                    <span className={gluceraTrendsCardLabel}>SpO2</span>
                  </div>
                  <span className={gluceraTrendsRowValue}>
                    {daily?.spo2_percent ? fmt(daily.spo2_percent, 1) : "—"}
                    <span className={cn(gluceraTrendsRowUnit, "ml-1")}>%</span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wind className="h-4 w-4 text-muted-foreground" />
                    <span className={gluceraTrendsCardLabel}>Breathing rate</span>
                  </div>
                  <span className={gluceraTrendsRowValue}>
                    {daily?.breathing_rate != null ? fmt(daily.breathing_rate, 1) : "—"}
                    <span className={cn(gluceraTrendsRowUnit, "ml-1")}>bpm</span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    <span className={gluceraTrendsCardLabel}>Distance</span>
                  </div>
                  <span className={gluceraTrendsRowValue}>
                    {daily?.distance_m != null ? daily.distance_m : "—"}
                    <span className={cn(gluceraTrendsRowUnit, "ml-1")}>m</span>
                  </span>
                </div>
              </div>

              {/* <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground/50 text-center border-t border-border/40 pt-3">
                Data from Fitbit · Synced today
              </p> */}
            </Card>

          </div>

          {/* bottom spacer */}
          <div className="h-4" />
          </>
          )}
        </div>
      </div>

      <BottomNavBar activeIndex={3} />

      <BridgeResponseDialog
        open={debugPopupOpen}
        title="Google Health Data"
        subtitle="Tap outside or Close to dismiss"
        loading={debugPopupLoading}
        loadingText="Calling Flutter getUserData…"
        data={debugPopupData}
        onClose={closeDebugPopup}
      />
    </div>
  );
};

export default TrendsPage;

